import {
  CrudID,
  IListTimesheetLockFetchParams,
  ITimesheetLock,
  ListContext,
  MN_TIMESHEET,
  MN_TIMESHEET_LOCK,
  Paths,
  useList,
} from '@jobcost-monorepo/crud';
import { createRepository } from '@jobcost-monorepo/crud/dto/repositoryFactory';
import {
  IListTimesheetFetchParams,
  ITimesheet,
} from '@jobcost-monorepo/crud/dto/timesheet';
import CrudForm from '@jobcost-monorepo/crud/ui/CrudForm';
import dayjs from '@jobcost-monorepo/dayjs-config';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { Loader, ModalDelete } from '@jobcost-monorepo/ui';
import {
  checkIfAdmin,
  ITimesheetsByResourceByDate,
  TimesheetSinglePdf,
} from '@jobcost-monorepo/utils';
import { useEffect, useRef, useState } from 'react';
import TimesheetSingleBody from './Body';
import TimesheetSingleHeader from './Header';
import { groupDataForDate } from './helperTimeSheetSingle';
import ReplicateForm from './ReplicateForm';
import ReactPDF from '@react-pdf/renderer';

const TimesheetSinglePage = () => {
  const isAdmin = checkIfAdmin();
  const ref = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const repository = createRepository<ITimesheet, IListTimesheetFetchParams>(
    MN_TIMESHEET,
  );
  const repositoryLock = createRepository<
    ITimesheetLock,
    IListTimesheetLockFetchParams
  >(MN_TIMESHEET_LOCK);
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [resourceIdSearched, setResourceIdSearched] = useState<
    number | undefined
  >();
  const [groupedData, setGroupedData] = useState<ITimesheetsByResourceByDate[]>(
    [],
  );
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [replicateOpen, setReplicateOpen] = useState<boolean>(false);
  const [lockModalOpen, setLockModalOpen] = useState<boolean>(false);
  const [isLocked, setIsLocked] = useState<boolean>(true);

  const {
    fetchDataList,
    setFormElement,
    setParams,
    dataList,
    deleteElement,
    formElement,
    params,
    loading,
    clearDataList,
  } = useList<ITimesheet, IListTimesheetFetchParams>({
    moduleName: MN_TIMESHEET,
    repository: repository,
    updateStore: false,
    forcedParams: {
      ...({
        isResourceMandatory: true,
      } as Record<Paths<IListTimesheetFetchParams>, any>),
    },
  });

  const {
    fetchDataList: fetchLocks,
    dataList: dataLocks,
    loading: loadingLocks,
  } = useList<ITimesheetLock, IListTimesheetLockFetchParams>({
    moduleName: MN_TIMESHEET_LOCK,
    repository: repositoryLock,
    updateStore: false,
  });

  const handleNew = (): void => {
    setFormOpen(true);
  };

  const handleReplicate = (): void => {
    setReplicateOpen(true);
  };

  const handleDeleteModal = (element: ITimesheet) => {
    setFormElement(element);
    setDeleteModalOpen(true);
  };

  const handleDelete = async (id: CrudID): Promise<void> => {
    await deleteElement(id);
    await fetchDataList({
      resourceId: resourceIdSearched,
      startDate: start,
      endDate: end,
    });
  };

  const handleSearch = async (
    resourceId: number,
    startDate: string,
    endDate: string,
    jobId?: number,
    orderId?: number,
  ) => {
    setStart(startDate);
    setEnd(endDate);
    setResourceIdSearched(resourceId);
    await fetchDataList({ resourceId, startDate, endDate, jobId, orderId });
    await fetchLocks({ resourceId, startDate, endDate });
  };

  const handleData = () => {
    const data = groupDataForDate(dataList.data);
    setGroupedData(data);
  };

  const handlePrint = () => {
    ReactPDF.pdf(
      <TimesheetSinglePdf
        dataList={groupedData}
        startDate={start}
        endDate={end}
      />,
    )
      .toBlob()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        iframe.src = url;
        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        };
      });
  };

  const getForm = () => {
    if (formOpen) return <CrudForm<ITimesheet, IListTimesheetFetchParams> />;
    if (replicateOpen)
      return <ReplicateForm setReplicateOpen={setReplicateOpen} />;
    return <div />;
  };

  const handleCheckLock = async () => {
    if (isAdmin) {
      await handleLockAndUnlock();
      return;
    }
    setLockModalOpen(true);
  };

  const handleLockAndUnlock = async () => {
    if (dataLocks && dataLocks.totalCount === 1) {
      await repositoryLock.delete(dataLocks.data[0].id).then().catch();
      await fetchLocks();
      return;
    }

    if (resourceIdSearched && start) {
      const item = {
        resourceId: resourceIdSearched,
        lockDate: dayjs.utc(start).toDate(),
      };

      await repositoryLock
        .create(item as ITimesheetLock)
        .then()
        .catch();
      await fetchLocks();
      return;
    }
  };

  const handleIsLocked = () => {
    if (!dataLocks || dataLocks.totalCount === 0) {
      setIsLocked(false);
      return;
    }

    if (dataLocks.totalCount === 1) {
      const lockData = dataLocks.data[0];
      setIsLocked(
        lockData.resourceId === resourceIdSearched &&
          dayjs.utc(lockData.lockDate).month() === dayjs.utc(start).month() &&
          dayjs.utc(lockData.lockDate).year() === dayjs.utc(start).year(),
      );
      return;
    }
    console.error('Errore nei dati');
    setIsLocked(true);
  };

  useEffect(() => {
    handleData();
  }, [JSON.stringify(dataList)]);

  useEffect(() => {
    handleIsLocked();
  }, [JSON.stringify(dataLocks)]);

  return (
    <ListContext.Provider
      value={{
        fetchDataList,
        setFormElement,
        setParams,
        dataList,
        deleteElement,
        formElement,
        params,
        loading,
        setFormOpen,
        setDeleteModalOpen,
        moduleName: MN_TIMESHEET,
        repository,
        updateStore: false,
        clearDataList,
      }}
    >
      <div className="flex flex-col m-4 sm:m-6 overflow-hidden h-full">
        {formOpen || replicateOpen ? (
          getForm()
        ) : (
          <>
            <TimesheetSingleHeader
              isLocked={isLocked}
              handleSearch={handleSearch}
              handleNew={handleNew}
              handlePrint={handlePrint}
              handleReplicate={handleReplicate}
              handleCheckLock={handleCheckLock}
            />
            <Loader showLoader={loading || loadingLocks} />
            {dataList.data.length ? (
              <TimesheetSingleBody
                ref={ref}
                setFormElement={setFormElement}
                setFormOpen={setFormOpen}
                handleDelete={handleDeleteModal}
                dataList={groupedData}
                startDate={start}
                endDate={end}
                isLocked={isLocked}
              />
            ) : (
              <div className="size-full flex items-center justify-center">
                {t('noRecordFound')}
              </div>
            )}
          </>
        )}
        {formElement && formElement.id && (
          <ModalDelete
            isOpen={deleteModalOpen}
            handleClose={() => setDeleteModalOpen(false)}
            handleDelete={() => handleDelete(formElement.id)}
            loader={loading}
          />
        )}

        <ModalDelete
          isOpen={lockModalOpen}
          handleClose={() => setLockModalOpen(false)}
          handleDelete={() => handleLockAndUnlock()}
          loader={loading}
          customTitle={'Blocca Foglio presenze'}
          customDescription={
            'Attenzione! Bloccando il foglio presenze si confermano le registrazioni effettuate e non sarà più possibile modificarle. Continuare?'
          }
          customCloseMessage={'Annulla'}
          customDeleteMessage={'Blocca'}
        />
      </div>
    </ListContext.Provider>
  );
};

export default TimesheetSinglePage;
